import React, { useState, useEffect } from "react";
import { Section } from "./UI";
import { commissionModels } from "../constants/leads";
import { MultiSelect } from "./MultiSelect";
import { eventsService } from "../services/eventsService";

export function ExportDialog({ onExport, onClose, exporting }) {
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [exportResult, setExportResult] = useState(null);
  const [copying, setCopying] = useState(false);
  const [eventsWithLeads, setEventsWithLeads] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  // Загружаем события с лидами при монтировании
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoadingEvents(true);
        const events = await eventsService.getEventsWithLeads();
        setEventsWithLeads(events);
      } catch (error) {
        console.error('Ошибка загрузки событий:', error);
      } finally {
        setLoadingEvents(false);
      }
    };

    loadEvents();
  }, []);

  const handleExport = async () => {
    const filters = {};
    
    // Передаем фильтр только если выбраны НЕ ВСЕ варианты
    if (selectedEvents.length > 0 && selectedEvents.length < eventsWithLeads.length) {
      filters.event = selectedEvents.join(',');
    }
    
    // Передаем фильтр только если выбраны НЕ ВСЕ модели
    if (selectedModels.length > 0 && selectedModels.length < commissionModels.length) {
      filters.commission_model = selectedModels.join(',');
    }

    const result = await onExport(filters);
    if (result) {
      setExportResult(result);
    }
  };

  const handleCopyLink = async () => {
    if (!exportResult?.download_url) return;
    
    try {
      setCopying(true);
      
      // Копируем ссылку в буфер обмена
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(exportResult.download_url);
      } else {
        // Fallback для старых браузеров
        const textarea = document.createElement('textarea');
        textarea.value = exportResult.download_url;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      
      // Показываем визуальную обратную связь
      const originalText = 'Скопировать ссылку';
      setCopying(false);
      
      // Можно показать toast, но для простоты просто меняем текст кнопки
      setTimeout(() => {
        setCopying(false);
      }, 2000);
      
    } catch (error) {
      console.error('Ошибка копирования:', error);
      setCopying(false);
    }
  };

  const handleDownload = () => {
    if (!exportResult?.download_url) return;
    
    if (window.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(exportResult.download_url);
    } else {
      window.open(exportResult.download_url, '_blank');
    }
  };

  const handleClose = () => {
    setExportResult(null);
    setSelectedEvents([]);
    setSelectedModels([]);
    onClose();
  };

  if (exportResult) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
          <h2 className="text-xl font-bold mb-4">✅ Экспорт готов</h2>
          
          <div className="space-y-3 mb-6">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
              <div className="text-sm text-gray-600 dark:text-gray-400">Количество лидов</div>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {exportResult.leads_count}
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
              <div className="text-sm text-gray-600 dark:text-gray-400">Размер файла</div>
              <div className="text-lg font-medium">
                {(exportResult.file_size / 1024).toFixed(2)} KB
              </div>
            </div>
            
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl">
              <div className="text-sm text-gray-600 dark:text-gray-400">Ссылка действительна</div>
              <div className="text-lg font-medium">
                {exportResult.expires_in_days} дней
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={handleDownload}
              className="w-full py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
            >
              📥 Скачать файл
            </button>
            
            <button
              onClick={handleCopyLink}
              disabled={copying}
              className="w-full py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              {copying ? '✓ Скопировано!' : '🔗 Скопировать ссылку'}
            </button>
            
            <button
              onClick={handleClose}
              className="w-full py-3 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-2xl w-full my-8">
        <h2 className="text-xl font-bold mb-4">📤 Экспорт в CSV</h2>
        
        <div className="space-y-6 mb-6">
          {/* Фильтр по событиям */}
          <MultiSelect
            label="События (не выбрано или выбраны все = экспорт всех)"
            options={eventsWithLeads.map(event => ({
              value: event.code,
              label: event.name
            }))}
            value={selectedEvents}
            onChange={setSelectedEvents}
            placeholder={loadingEvents ? "Загрузка..." : "Все события"}
          />

          {/* Фильтр по моделям комиссии */}
          <MultiSelect
            label="Модели работы (не выбрано или выбраны все = экспорт всех)"
            options={commissionModels.map(model => ({
              value: model,
              label: model
            }))}
            value={selectedModels}
            onChange={setSelectedModels}
            placeholder="Все модели"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? 'Экспорт...' : 'Экспортировать'}
          </button>
          <button
            onClick={handleClose}
            disabled={exporting}
            className="flex-1 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
